/**
 * Firebase frontend module — Auth only.
 *
 * All Firestore, Storage and Functions operations have been moved to the
 * Cloudflare Pages Functions backend and are accessed through the API client
 * in @/services/api.  This file retains only Firebase Auth (needed for the
 * OTP sign-in flow and ID-token generation) plus a Timestamp compatibility
 * class so existing components do not need to be rewritten.
 */

// ─────────────────────────────────────────────────────────────────────────────
// Firebase Auth init
// ─────────────────────────────────────────────────────────────────────────────
import { initializeApp } from "firebase/app";
import {
  getAuth,
  signOut,
  onAuthStateChanged,
  signInWithCustomToken,
  type User,
} from "firebase/auth";
export { signInWithCustomToken };
import { initializeAppCheck, ReCaptchaV3Provider } from "firebase/app-check";
import { logger } from "./logger";
import { captureHandledException } from "./sentry";

import {
  listDocs,
  getDoc as apiGetDoc,
  createDoc,
  updateDoc as apiUpdateDoc,
  deleteDoc as apiDeleteDoc,
  setDocById,
  cascadeUpdate,
  apiGet,
  apiPost,
  apiUploadFile,
} from "@/services/api";

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: `${import.meta.env.VITE_FIREBASE_PROJECT_ID}.firebaseapp.com`,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: `${import.meta.env.VITE_FIREBASE_PROJECT_ID}.firebasestorage.app`,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);

// Initialize App Check with reCAPTCHA v3.
// Hosted builds attest with reCAPTCHA on the Cloudflare Pages domains. The local
// dev server can't (localhost is deliberately not on the reCAPTCHA allowlist), so
// it uses a debug token registered in the Firebase console and kept in .dev.vars
// (see README). Vite replaces import.meta.env.DEV with false in builds, so the
// debug-token branch is stripped from every deployed bundle.
import { getToken as getAppCheckTokenFromSDK, type AppCheck } from "firebase/app-check";

let _appCheck: AppCheck | null = null;

function prepareAppCheck(): boolean {
  if (!import.meta.env.VITE_RECAPTCHA_SITE_KEY) return false;
  if (!import.meta.env.DEV) return true;

  const debugToken = import.meta.env.VITE_APPCHECK_DEBUG_TOKEN;
  if (!debugToken) {
    logger.warn(
      'App Check is off locally: set VITE_APPCHECK_DEBUG_TOKEN in .dev.vars to a debug ' +
      'token registered in the Firebase console (see README). Requests that need App Check will fail.',
    );
    return false;
  }
  // Must be set before initializeAppCheck. Never set this to `true`: that makes
  // the SDK mint a new token instead of using the registered one.
  (self as any).FIREBASE_APPCHECK_DEBUG_TOKEN = debugToken;
  hideAppCheckDebugTokenLog();
  return true;
}

/**
 * The App Check SDK always console.logs the debug token in debug mode, even one
 * we supplied. It's a secret, so drop that line and log a redacted note instead.
 * Dev only: called from the import.meta.env.DEV branch above.
 */
function hideAppCheckDebugTokenLog(): void {
  const log = console.log.bind(console);
  console.log = (...args: unknown[]) => {
    if (typeof args[0] === 'string' && args[0].startsWith('App Check debug token:')) {
      log('App Check debug token loaded from .dev.vars (value hidden).');
      return;
    }
    log(...args);
  };
}

if (prepareAppCheck()) {
  try {
    _appCheck = initializeAppCheck(app, {
      provider: new ReCaptchaV3Provider(import.meta.env.VITE_RECAPTCHA_SITE_KEY),
      // Disable proactive background refresh. The SDK will still fetch a token
      // on demand when getAppCheckToken() is called (e.g. before a storage
      // upload). Eager auto-refresh fires immediately on init and causes
      // repeated 403 console errors whenever the reCAPTCHA domain allowlist
      // hasn't been updated yet, or while a 24-hour throttle is active.
      isTokenAutoRefreshEnabled: false,
    });
  } catch (e) {
    captureHandledException(e, "firebase.app-check.initialize");
    logger.warn('App Check initialization failed — continuing without it:', e);
  }
}

/**
 * Get the current App Check token string, or null if App Check is not
 * initialised (e.g. VITE_RECAPTCHA_SITE_KEY not set or init failed).
 * Used to attach X-Firebase-AppCheck headers to direct Firebase REST calls.
 */
export async function getAppCheckToken(): Promise<string | null> {
  if (!_appCheck) return null;
  try {
    const result = await getAppCheckTokenFromSDK(_appCheck);
    return result.token;
  } catch (error) {
    captureHandledException(error, "firebase.app-check.token");
    return null;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Timestamp compatibility class
// Replaces the Firestore Timestamp so existing components keep working.
// Serialises to an explicit marker object so the REST backend can preserve
// Firestore timestamp semantics instead of treating the value as a string.
// ─────────────────────────────────────────────────────────────────────────────
export class Timestamp {
  private readonly _ms: number;

  constructor(seconds: number, nanoseconds = 0) {
    this._ms = seconds * 1000 + Math.floor(nanoseconds / 1_000_000);
  }

  toDate(): Date {
    return new Date(this._ms);
  }

  toMillis(): number {
    return this._ms;
  }

  /** Called by JSON.stringify — preserves timestamp intent across the API. */
  toJSON(): { __firestoreTimestamp: string } {
    return { __firestoreTimestamp: this.toDate().toISOString() };
  }

  toString(): string {
    return this.toDate().toISOString();
  }

  static fromDate(date: Date): Timestamp {
    const ms = date.getTime();
    return new Timestamp(Math.floor(ms / 1000), (ms % 1000) * 1_000_000);
  }

  static now(): Timestamp {
    return Timestamp.fromDate(new Date());
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// deleteField sentinel
// The backend detects "__DELETE_FIELD__" and converts it to FieldValue.delete()
// ─────────────────────────────────────────────────────────────────────────────
export const DELETE_FIELD_SENTINEL = "__DELETE_FIELD__";
// Return type `any` keeps existing component code type-checking without changes.
export function deleteField(): any {
  return DELETE_FIELD_SENTINEL;
}

// ─────────────────────────────────────────────────────────────────────────────
// Deserialization — convert ISO date strings in API responses to Timestamps
// ─────────────────────────────────────────────────────────────────────────────
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z$/;

function deserialize<T>(data: unknown): T {
  if (data === null || data === undefined) return data as T;
  if (typeof data === "string" && ISO_DATE_RE.test(data)) {
    return Timestamp.fromDate(new Date(data)) as unknown as T;
  }
  if (Array.isArray(data)) return data.map(deserialize) as unknown as T;
  if (typeof data === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(data as Record<string, unknown>)) {
      out[k] = deserialize(v);
    }
    return out as T;
  }
  return data as T;
}

function d<T>(raw: unknown): T {
  return deserialize<T>(raw);
}

// ─────────────────────────────────────────────────────────────────────────────
// Auth functions
// ─────────────────────────────────────────────────────────────────────────────
export const signOutUser = async () => signOut(auth);
export const onAuthChange = (
  callback: (user: User | null) => void | Promise<void>,
  onError?: (error: Error) => void,
) =>
  onAuthStateChanged(
    auth,
    (user) => {
      try {
        const result = callback(user);
        if (result && typeof result.then === "function") {
          void result.catch((error) => {
            captureHandledException(error, "firebase.auth-state-callback");
          });
        }
      } catch (error) {
        captureHandledException(error, "firebase.auth-state-callback");
      }
    },
    (error) => {
      captureHandledException(error, "firebase.auth-state");
      onError?.(error);
    },
  );

// ─────────────────────────────────────────────────────────────────────────────
// Timestamp helpers (kept for backward compatibility)
// ─────────────────────────────────────────────────────────────────────────────
export const stringToTimestamp = (dateString: string): Timestamp =>
  Timestamp.fromDate(new Date(dateString));

export const timestampToString = (timestamp: Timestamp): string =>
  timestamp.toDate().toISOString().split("T")[0];

// ─────────────────────────────────────────────────────────────────────────────
// Interfaces (unchanged)
// ─────────────────────────────────────────────────────────────────────────────
export interface FirebaseUser {
  uid: string;
  email: string;
  displayName?: string;
}

export interface VideoData {
  id?: string;
  externalId: string;
  title: string;
  description?: string;
  author: string;
  authorId?: string;
  authorName?: string;
  authorImageUrl?: string;
  contributorIds?: string[];
  contributors?: AuthorData[];
  channel: string;
  duration: string;
  mediaType: "youtube" | "vimeo" | "article";
  contentCategories: string[];
  contentLibraries?: string[];
  modules?: string[];
  contentType: string;
  jobTypes: string[];
  enabled: boolean;
  status: "Draft" | "Scheduled" | "Published" | "Blocked";
  url: string;
  content: string;
  publishDate?: Timestamp;
  added?: any;
  modified?: any;
  externalTags?: string[];
  thumbnailUrl?: string;
  directFileUrl?: string;
  episodeOnly?: boolean;
}

export interface CategoryData {
  id?: string;
  title: string;
  description?: string;
  contentLibrary: string;
  module: string;
  tag: string;
  displayOrder?: number;
  mediaTypes?: string[];
}

export interface ContentLibraryData {
  id?: string;
  title?: string;
  name?: string;
  module: string;
  description?: string;
  key?: string;
  enabled?: boolean;
  displayOrder?: number;
  libraryType?: string;
}

export interface AuthorData {
  id?: string;
  name: string;
  profilePicUrl: string;
  story: string;
  qualifications: string;
}

export interface BadgeData {
  id?: string;
  colour: "gold" | "silver" | "bronze" | "green" | "purple" | "blue" | "pink" | "yellow";
  description: string;
  messageOnEarning: string;
  enabled: boolean;
  imageUrl?: string;
  key: string;
  name: string;
  type: "program" | "steps" | "streak" | "yoga" | "community" | "content";
  sortOrder?: number;
}

export interface MilestoneData {
  id?: string;
  badgeId?: string;
  enabled: boolean;
  programId?: string;
  programName?: string;
  programTaskNumber?: string;
  streakType?: "dailyTask" | "steps" | "program";
  type: "program" | "streak" | "action";
  appEventType?: string;
  eventType?: string;
  userActionType?: string;
  value: number;
}

export interface AppUser {
  id: string;
  firstName?: string;
  secondName?: string;
  email: string;
  displayName?: string;
}

export interface AdminUser {
  id?: string;
  uid: string;
  adminRole: string;
}

export interface UserWithPermissions extends AppUser {
  permissions: string[];
}

export interface AuthUser {
  uid: string;
  email: string;
  displayName?: string;
  disabled?: boolean;
}

export interface SurveyQuestionCategoryData {
  id?: string;
  key: string;
  name: string;
  description: string;
  displayOrder: number;
  created?: Timestamp;
  modified?: Timestamp;
}

export interface SurveyQuestionData {
  id?: string;
  questionText: string;
  additionalNotes?: string;
  inputType: "outOfFive" | "text" | "yesNo";
  categoryId: string;
  tags: string[];
  highPositive: boolean;
  includeNotes: boolean;
  drivesRecommendations: boolean;
  lowValueLabel?: string;
  highValueLabel?: string;
  created?: Timestamp;
  modified?: Timestamp;
}

export interface SurveyTemplateQuestion {
  questionId: string;
  displayOrder: number;
}

export type SurveyFrequency =
  | "weekly" | "fortnightly" | "monthly" | "bimonthly"
  | "quarterly" | "six months" | "annually";

export interface SurveyPresetTemplateData {
  id?: string;
  name: string;
  openingMessage: string;
  questions: SurveyTemplateQuestion[];
  frequency: SurveyFrequency;
  percentageOfWorkforce: number;
  promptType: "passive" | "prompted" | "none";
  displayType?: "inline" | "wizard";
  commsEnabled: "email" | "push notification" | "none";
  status: "active" | "disabled";
  created?: Timestamp;
  modified?: Timestamp;
}

export interface AuditChange {
  field: string;
  from: string;
  to: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Content (videos / articles)
// ─────────────────────────────────────────────────────────────────────────────
export const saveVideo = async (videoData: VideoData) => {
  const result = await createDoc<VideoData>("content", videoData);
  return d<VideoData>(result);
};

// Articles are attributed via `contributors` and are saved without a legacy `author`.
export const addVideo = async (
  _userId: string,
  videoData: Omit<VideoData, "author"> & { author?: string },
): Promise<string> => {
  const result = await createDoc<VideoData>("content", videoData);
  return (d<VideoData>(result) as any).id as string;
};

export const updateVideo = async (videoId: string, updates: Partial<VideoData>) => {
  await apiUpdateDoc("content", videoId, updates);
};

export const deleteVideo = async (videoId: string) => {
  await apiDeleteDoc("content", videoId);
};

export const getUserVideos = async (_userId?: string): Promise<VideoData[]> => {
  // Gets all videos; client-side filter mirrors previous query
  const all = await listDocs<VideoData>("content");
  return d<VideoData[]>(all).filter(
    (v) => (v.mediaType === "youtube" || v.mediaType === "vimeo") && v.enabled,
  );
};

export const getUserArticles = async (_userId?: string): Promise<VideoData[]> => {
  const all = await listDocs<VideoData>("content");
  return d<VideoData[]>(all).filter((v) => v.mediaType === "article" && v.enabled);
};

export const getVideoById = async (videoId: string): Promise<VideoData | null> => {
  const result = await apiGetDoc<VideoData>("content", videoId);
  return result ? d<VideoData>(result) : null;
};

export const getArticleById = async (articleId: string): Promise<VideoData | null> => {
  const result = await apiGetDoc<VideoData>("content", articleId);
  return result ? d<VideoData>(result) : null;
};

/** Returns all content documents (no filter). */
export const getAllContent = async (): Promise<VideoData[]> => {
  const all = await listDocs<VideoData>("content");
  return d<VideoData[]>(all);
};

/** Returns content documents by IDs (used by program component-modal). */
export const getContentByIds = async (ids: string[]): Promise<VideoData[]> => {
  const results = await Promise.all(ids.map((id) => apiGetDoc<VideoData>("content", id)));
  return d<VideoData[]>(results.filter(Boolean) as VideoData[]);
};

/** Returns vimeo content that is enabled (used by program steps). */
export const getVimeoContent = async (): Promise<VideoData[]> => {
  const all = await listDocs<VideoData>("content", { field: "mediaType", value: "vimeo" });
  return d<VideoData[]>(all).filter((v) => v.enabled);
};

/** Returns video-series content for given contentLibraries. */
export const getVideoSeriesContent = async (
  contentLibraryIds: string[],
): Promise<VideoData[]> => {
  const all = await listDocs<VideoData>("content");
  return d<VideoData[]>(all).filter(
    (v) =>
      v.mediaType === ("videoSeries" as any) &&
      v.contentLibraries?.some((lib) => contentLibraryIds.includes(lib)),
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Categories
// ─────────────────────────────────────────────────────────────────────────────
export const saveCategory = async (categoryData: CategoryData) => {
  return createDoc("contentCategories", categoryData);
};

export const updateCategory = async (categoryId: string, updates: Partial<CategoryData>) => {
  const current = await apiGetDoc<CategoryData>("contentCategories", categoryId);
  if (!current) throw new Error("Category not found");
  if (updates.module || updates.contentLibrary) {
    return cascadeUpdate("contentCategories", categoryId, { tag: (current as any).tag, ...updates });
  }
  return apiUpdateDoc("contentCategories", categoryId, updates);
};

export const deleteCategory = async (categoryId: string) => {
  return apiDeleteDoc("contentCategories", categoryId);
};

export const getUserCategories = async (_userId?: string): Promise<CategoryData[]> => {
  const all = await listDocs<CategoryData>("contentCategories");
  return d<CategoryData[]>(all).filter((cat) => !cat.mediaTypes?.includes("videoSeries"));
};

export const getVideoSeriesCategoriesForLibraries = async (
  libraryIdentifiers: string[],
): Promise<CategoryData[]> => {
  if (libraryIdentifiers.length === 0) return [];
  const all = await listDocs<CategoryData>("contentCategories", {
    field: "mediaTypes",
    value: "videoSeries",
    op: "array-contains",
  });
  return d<CategoryData[]>(all).filter((cat) =>
    libraryIdentifiers.includes(cat.contentLibrary),
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Content Libraries
// ─────────────────────────────────────────────────────────────────────────────
export const getContentLibraries = async (): Promise<ContentLibraryData[]> => {
  const all = await listDocs<ContentLibraryData>("contentLibraries");
  return d<ContentLibraryData[]>(all);
};

export const getContentLibrariesByModule = async (module: string): Promise<ContentLibraryData[]> => {
  const all = await listDocs<ContentLibraryData>("contentLibraries", {
    field: "module",
    value: module,
  });
  return d<ContentLibraryData[]>(all);
};

export const getEnabledContentLibraries = async (): Promise<ContentLibraryData[]> => {
  const all = await listDocs<ContentLibraryData>("contentLibraries", {
    field: "enabled",
    value: "true",
  });
  return d<ContentLibraryData[]>(all);
};

// ─────────────────────────────────────────────────────────────────────────────
// Authors / Contributors
// ─────────────────────────────────────────────────────────────────────────────
export const saveAuthor = async (authorData: AuthorData) => {
  return createDoc("contributors", authorData);
};

export const updateAuthor = async (authorId: string, updates: Partial<AuthorData>) => {
  // The cascade endpoint handles both update + content-record cascade server-side
  return cascadeUpdate("contributors", authorId, updates);
};

export const deleteAuthor = async (authorId: string) => {
  return apiDeleteDoc("contributors", authorId);
};

export const getAuthors = async (): Promise<AuthorData[]> => {
  const all = await listDocs<AuthorData>("contributors");
  return d<AuthorData[]>(all);
};

export const getAuthorById = async (authorId: string): Promise<AuthorData | null> => {
  const result = await apiGetDoc<AuthorData>("contributors", authorId);
  return result ? d<AuthorData>(result) : null;
};

// ─────────────────────────────────────────────────────────────────────────────
// File uploads  (all go through /api/storage/upload)
// ─────────────────────────────────────────────────────────────────────────────
export const uploadAuthorProfilePicture = async (file: File, authorId?: string): Promise<string> => {
  const { url } = await apiUploadFile(file, "author", authorId);
  return url;
};

export const uploadArticleThumbnail = async (file: File, articleId?: string): Promise<string> => {
  const { url } = await apiUploadFile(file, "article", articleId);
  return url;
};

export const uploadArticleContentImage = async (file: File, articleId?: string): Promise<string> => {
  const { url } = await apiUploadFile(file, "articleContent", articleId);
  return url;
};

export const uploadProgramBanner = async (file: File, programId?: string): Promise<string> => {
  const { url } = await apiUploadFile(file, "program", programId);
  return url;
};

export const uploadBadgeImage = async (file: File, badgeKey?: string): Promise<string> => {
  const { url } = await apiUploadFile(file, "badge", badgeKey);
  return url;
};

export const uploadTaskThumbnail = async (file: File, taskId?: string): Promise<string> => {
  const { url } = await apiUploadFile(file, "task", taskId);
  return url;
};

export const uploadSeriesImage = async (file: File, seriesId?: string): Promise<string> => {
  const { url } = await apiUploadFile(file, "series", seriesId);
  return url;
};

export const uploadEventBackground = async (file: File, eventId?: string): Promise<string> => {
  const { url } = await apiUploadFile(file, "eventBackground", eventId);
  return url;
};

export const uploadEventImage = async (file: File, eventId?: string): Promise<string> => {
  const { url } = await apiUploadFile(file, "eventImage", eventId);
  return url;
};

export const uploadPromotionImage = async (file: File, promoId?: string): Promise<string> => {
  const { url } = await apiUploadFile(file, "promotion", promoId);
  return url;
};

export const uploadNewsletterImage = async (file: File, newsletterId?: string): Promise<string> => {
  const { url } = await apiUploadFile(file, "newsletter", newsletterId);
  return url;
};

export const uploadPartnershipLogo = async (file: File, partnerId?: string): Promise<string> => {
  const { url } = await apiUploadFile(file, "partnershipLogo", partnerId);
  return url;
};

// ─────────────────────────────────────────────────────────────────────────────
// User management
// ─────────────────────────────────────────────────────────────────────────────

/** Returns the current authenticated user's admin roles. */
export const getUserPermissions = async (_uid?: string): Promise<string[]> => {
  try {
    const result = await apiGet<{ roles: string[] }>("/api/auth/permissions");
    return result.roles ?? [];
  } catch (error) {
    captureHandledException(error, "firebase.user-permissions");
    // Log with full detail so it's visible in the browser console on production
    const msg = error instanceof Error ? error.message : String(error);
    console.error("[getUserPermissions] Failed to fetch roles — user will see Access Denied:", msg, error);
    return [];
  }
};

/** Looks up the admin role(s) for a specific user by their Firebase UID.
 *  Used by admin UI flows that need another user's existing role. */
export const getUserRoleByUid = async (uid: string): Promise<string[]> => {
  try {
    const all = await listDocs<AdminUser>("adminUsers", { field: "uid", value: uid });
    if (all.length > 0 && all[0].adminRole) return [all[0].adminRole];
    return [];
  } catch (error) {
    captureHandledException(error, "firebase.user-role");
    logger.error("Error fetching user role by uid:", error);
    return [];
  }
};

export const getUsersWithPermissions = async (): Promise<UserWithPermissions[]> => {
  try {
    const result = await apiGet<UserWithPermissions[]>("/api/auth/users-with-permissions");
    return d<UserWithPermissions[]>(result);
  } catch (error) {
    captureHandledException(error, "firebase.users-with-permissions");
    logger.error("Error fetching users with permissions:", error);
    return [];
  }
};

export const updateUserPermissions = async (uid: string, permissions: string[], email?: string): Promise<void> => {
  await apiPost("/api/auth/update-permissions", { uid, permissions, email });
};

export const searchAuthUsers = async (emailQuery: string): Promise<AuthUser[]> => {
  if (!emailQuery || emailQuery.length < 3) return [];
  try {
    const all = await listDocs<any>("appUsers");
    const lower = emailQuery.toLowerCase();
    const seen = new Set<string>();
    const result: AuthUser[] = [];
    for (const doc of all) {
      const email = (doc.email || "").toLowerCase();
      if (email.includes(lower) && !seen.has(email)) {
        seen.add(email);
        result.push({
          uid: doc.id,
          email: doc.email,
          displayName:
            doc.firstName && doc.secondName
              ? `${doc.firstName} ${doc.secondName}`
              : undefined,
        });
      }
    }
    return result;
  } catch (error) {
    captureHandledException(error, "firebase.search-auth-users");
    logger.error("Error searching auth users:", error);
    return [];
  }
};

export const checkUserHasPermissions = async (uid: string): Promise<boolean> => {
  try {
    const all = await listDocs<AdminUser>("adminUsers", { field: "uid", value: uid });
    return all.length > 0;
  } catch (error) {
    captureHandledException(error, "firebase.check-user-permissions");
    return false;
  }
};

export const addUserWithPermissions = async (uid: string, permissions: string[], email?: string): Promise<void> => {
  await apiPost("/api/auth/add-permissions", { uid, permissions, email });
};

export const removeUserPermissions = async (uid: string): Promise<void> => {
  await apiPost("/api/auth/remove-permissions", { uid });
};

/** Returns available admin roles from the adminRoles collection. */
export const getAdminRoles = async (): Promise<any[]> => {
  const all = await listDocs<any>("adminRoles");
  return d<any[]>(all);
};

// ─────────────────────────────────────────────────────────────────────────────
// Badges
// ─────────────────────────────────────────────────────────────────────────────
export const getBadges = async (): Promise<BadgeData[]> => {
  const all = await listDocs<BadgeData>("badges");
  return d<BadgeData[]>(all);
};

export const getBadgeById = async (badgeId: string): Promise<BadgeData | null> => {
  const result = await apiGetDoc<BadgeData>("badges", badgeId);
  return result ? d<BadgeData>(result) : null;
};

export const checkBadgeKeyExists = async (key: string, excludeId?: string): Promise<boolean> => {
  const all = await listDocs<BadgeData>("badges", { field: "key", value: key });
  if (excludeId && all.length === 1) return (all[0] as any).id !== excludeId;
  return all.length > 0;
};

export const saveBadge = async (badgeData: Omit<BadgeData, "id">): Promise<string> => {
  const result = await createDoc<BadgeData>("badges", badgeData);
  return (result as any).id as string;
};

export const updateBadge = async (badgeId: string, updates: Partial<BadgeData>): Promise<void> => {
  await apiUpdateDoc("badges", badgeId, updates);
};

export const deleteBadge = async (badgeId: string): Promise<void> => {
  await apiDeleteDoc("badges", badgeId);
};

// ─────────────────────────────────────────────────────────────────────────────
// Milestones
// ─────────────────────────────────────────────────────────────────────────────
export const getMilestones = async (): Promise<MilestoneData[]> => {
  const all = await listDocs<MilestoneData>("milestones");
  return d<MilestoneData[]>(all);
};

export const getMilestoneById = async (milestoneId: string): Promise<MilestoneData | null> => {
  const result = await apiGetDoc<MilestoneData>("milestones", milestoneId);
  return result ? d<MilestoneData>(result) : null;
};

export const saveMilestone = async (data: Omit<MilestoneData, "id">): Promise<string> => {
  const result = await createDoc<MilestoneData>("milestones", data);
  return (result as any).id as string;
};

export const updateMilestone = async (milestoneId: string, updates: Partial<MilestoneData>): Promise<void> => {
  await apiUpdateDoc("milestones", milestoneId, updates);
};

export const deleteMilestone = async (milestoneId: string): Promise<void> => {
  await apiDeleteDoc("milestones", milestoneId);
};

// ─────────────────────────────────────────────────────────────────────────────
// Programs
// ─────────────────────────────────────────────────────────────────────────────
export const getPrograms = async (): Promise<any[]> => {
  const all = await listDocs<any>("programs");
  return d<any[]>(all);
};

export const getProgram = async (programId: string): Promise<any | null> => {
  const result = await apiGetDoc<any>("programs", programId);
  return result ? d<any>(result) : null;
};

export const addProgram = async (data: any): Promise<string> => {
  const result = await createDoc<any>("programs", data);
  return (result as any).id as string;
};

export const updateProgram = async (programId: string, data: any): Promise<void> => {
  await apiUpdateDoc("programs", programId, data);
};

// ─────────────────────────────────────────────────────────────────────────────
// Pulse Survey — Question Categories
// ─────────────────────────────────────────────────────────────────────────────
export const getSurveyQuestionCategories = async (): Promise<SurveyQuestionCategoryData[]> => {
  const all = await listDocs<SurveyQuestionCategoryData>("surveyQuestionCategories");
  return d<SurveyQuestionCategoryData[]>(all);
};

export const saveSurveyQuestionCategory = async (key: string, data: Omit<SurveyQuestionCategoryData, "id" | "key" | "created" | "modified">): Promise<void> => {
  await setDocById("surveyQuestionCategories", key, {
    ...data,
    created: Timestamp.now(),
    modified: Timestamp.now(),
  });
};

export const updateSurveyQuestionCategory = async (key: string, data: Partial<Omit<SurveyQuestionCategoryData, "id" | "key" | "created" | "modified">>): Promise<void> => {
  await apiUpdateDoc("surveyQuestionCategories", key, { ...data, modified: Timestamp.now() });
};

export const deleteSurveyQuestionCategory = async (key: string): Promise<void> => {
  await apiDeleteDoc("surveyQuestionCategories", key);
};

export const checkSurveyQuestionCategoryKeyExists = async (key: string): Promise<boolean> => {
  const result = await apiGetDoc<any>("surveyQuestionCategories", key);
  return result !== null;
};

export const checkSurveyQuestionCategoryNameExists = async (name: string, excludeKey?: string): Promise<boolean> => {
  const all = await listDocs<SurveyQuestionCategoryData>("surveyQuestionCategories", {
    field: "name",
    value: name,
  });
  if (excludeKey) return all.some((d: any) => d.id !== excludeKey);
  return all.length > 0;
};

// ─────────────────────────────────────────────────────────────────────────────
// Pulse Survey — Questions
// ─────────────────────────────────────────────────────────────────────────────
export const getSurveyQuestions = async (): Promise<SurveyQuestionData[]> => {
  const all = await listDocs<SurveyQuestionData>("surveyQuestions");
  return d<SurveyQuestionData[]>(all);
};

export const getSurveyQuestionsByCategory = async (categoryId: string): Promise<SurveyQuestionData[]> => {
  const all = await listDocs<SurveyQuestionData>("surveyQuestions", { field: "categoryId", value: categoryId });
  return d<SurveyQuestionData[]>(all);
};

export const saveSurveyQuestion = async (data: Omit<SurveyQuestionData, "id" | "created" | "modified">): Promise<string> => {
  const result = await createDoc<SurveyQuestionData>("surveyQuestions", {
    ...data,
    created: Timestamp.now(),
    modified: Timestamp.now(),
  });
  return (result as any).id as string;
};

export const updateSurveyQuestion = async (id: string, data: Partial<Omit<SurveyQuestionData, "id" | "created" | "modified">>): Promise<void> => {
  await apiUpdateDoc("surveyQuestions", id, { ...data, modified: Timestamp.now() });
};

export const deleteSurveyQuestion = async (id: string): Promise<void> => {
  await apiDeleteDoc("surveyQuestions", id);
};

// ─────────────────────────────────────────────────────────────────────────────
// Pulse Survey — Preset Templates
// ─────────────────────────────────────────────────────────────────────────────
export const getSurveyPresetTemplates = async (): Promise<SurveyPresetTemplateData[]> => {
  const all = await listDocs<SurveyPresetTemplateData>("surveyPresetTemplates");
  return d<SurveyPresetTemplateData[]>(all);
};

export const saveSurveyPresetTemplate = async (data: Omit<SurveyPresetTemplateData, "id" | "created" | "modified">): Promise<string> => {
  const result = await createDoc<SurveyPresetTemplateData>("surveyPresetTemplates", {
    ...data,
    created: Timestamp.now(),
    modified: Timestamp.now(),
  });
  return (result as any).id as string;
};

export const updateSurveyPresetTemplate = async (id: string, data: Partial<Omit<SurveyPresetTemplateData, "id" | "created" | "modified">>): Promise<void> => {
  await apiUpdateDoc("surveyPresetTemplates", id, { ...data, modified: Timestamp.now() });
};

export const deleteSurveyPresetTemplate = async (id: string): Promise<void> => {
  await apiDeleteDoc("surveyPresetTemplates", id);
};

// ─────────────────────────────────────────────────────────────────────────────
// Company Audit Logs
// ─────────────────────────────────────────────────────────────────────────────
export const writeCompanyAudit = async (
  companyId: string,
  companyName: string,
  action: string,
  performedBy: string,
  performedByUid: string,
  changes?: AuditChange[],
): Promise<void> => {
  try {
    await createDoc("companyAuditLogs", {
      companyId,
      companyName,
      action,
      performedBy,
      performedByUid,
      timestamp: Timestamp.now(),
      ...(changes && changes.length > 0 ? { changes } : {}),
    });
  } catch (err) {
    logger.error("Failed to write company audit log:", err);
  }
};

export const getCompanyAuditLogs = async (): Promise<any[]> => {
  const all = await listDocs<any>("companyAuditLogs");
  return d<any[]>(all).sort(
    (a, b) =>
      (b.timestamp?.toMillis?.() ?? 0) - (a.timestamp?.toMillis?.() ?? 0),
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Partnerships
// ─────────────────────────────────────────────────────────────────────────────
export const getPartnerships = async (): Promise<any[]> => {
  const all = await listDocs<any>("partnerships");
  return d<any[]>(all).sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
};

export const addPartnership = async (data: any): Promise<string> => {
  const result = await createDoc<any>("partnerships", data);
  return (result as any).id as string;
};

export const updatePartnership = async (id: string, data: any): Promise<void> => {
  await apiUpdateDoc("partnerships", id, data);
};

export const deletePartnership = async (id: string): Promise<void> => {
  await apiDeleteDoc("partnerships", id);
};

// ─────────────────────────────────────────────────────────────────────────────
// Companies
// ─────────────────────────────────────────────────────────────────────────────
export const getCompanies = async (): Promise<any[]> => {
  const all = await listDocs<any>("companies");
  return d<any[]>(all).sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
};

export const getCompany = async (id: string): Promise<any | null> => {
  const result = await apiGetDoc<any>("companies", id);
  return result ? d<any>(result) : null;
};

export const updateCompany = async (id: string, data: any): Promise<void> => {
  await apiUpdateDoc("companies", id, data);
};

export const deleteCompany = async (id: string): Promise<void> => {
  await apiDeleteDoc("companies", id);
};

export const getCompanyUserInvitations = async (companyId?: string): Promise<any[]> => {
  const all = companyId
    ? await listDocs<any>("companyUserInvitations", { field: "companyId", value: companyId })
    : await listDocs<any>("companyUserInvitations");
  return d<any[]>(all);
};

// ─────────────────────────────────────────────────────────────────────────────
// App Users
// ─────────────────────────────────────────────────────────────────────────────
export const getAppUsersByCompany = async (companyId: string): Promise<any[]> => {
  const all = await listDocs<any>("appUsers", { field: "companyId", value: companyId });
  return d<any[]>(all);
};

export const updateAppUser = async (id: string, data: any): Promise<void> => {
  await apiUpdateDoc("appUsers", id, data);
};

export const getAppUserByEmail = async (email: string): Promise<any | null> => {
  const all = await listDocs<any>("appUsers", { field: "email", value: email });
  return all.length > 0 ? d<any>(all[0]) : null;
};

export const getAppEventsByUser = async (uid: string): Promise<any[]> => {
  const all = await listDocs<any>("appEvents", { field: "uid", value: uid });
  return d<any[]>(all);
};

export const getActiveUsersCount = async (): Promise<number> => {
  const all = await listDocs<any>("appUsers", { field: "enabled", value: "true" });
  return all.filter((u: any) => u.userStatus === "signUpComplete").length;
};

export const getAppEvents = async (companyId?: string): Promise<any[]> => {
  const all = companyId
    ? await listDocs<any>("appEvents", { field: "companyId", value: companyId })
    : await listDocs<any>("appEvents");
  return d<any[]>(all);
};

// ─────────────────────────────────────────────────────────────────────────────
// Feature Groups and Feature Keys (Settings)
// ─────────────────────────────────────────────────────────────────────────────
export const getFeatureGroups = async (): Promise<any[]> => {
  const all = await listDocs<any>("featureGroups");
  return d<any[]>(all).sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
};

export const addFeatureGroup = async (key: string, data: any): Promise<void> => {
  await setDocById("featureGroups", key, {
    ...data,
    created: Timestamp.now(),
    modified: Timestamp.now(),
  });
};

export const updateFeatureGroup = async (id: string, data: any): Promise<void> => {
  await apiUpdateDoc("featureGroups", id, data);
};

export const deleteFeatureGroup = async (id: string): Promise<void> => {
  await apiDeleteDoc("featureGroups", id);
};

export const getFeatureKeys = async (): Promise<any[]> => {
  const all = await listDocs<any>("featureKeys");
  return d<any[]>(all).sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
};

export const addFeatureKey = async (data: any): Promise<string> => {
  const result = await createDoc<any>("featureKeys", data);
  return (result as any).id as string;
};

export const updateFeatureKey = async (id: string, data: any): Promise<void> => {
  await apiUpdateDoc("featureKeys", id, data);
};

export const deleteFeatureKey = async (id: string): Promise<void> => {
  await apiDeleteDoc("featureKeys", id);
};

// ─────────────────────────────────────────────────────────────────────────────
// Community Events
// ─────────────────────────────────────────────────────────────────────────────
export const getCommunityEvents = async (): Promise<any[]> => {
  const all = await listDocs<any>("communityEvents");
  return d<any[]>(all);
};

export const getCommunityEvent = async (id: string): Promise<any | null> => {
  const result = await apiGetDoc<any>("communityEvents", id);
  return result ? d<any>(result) : null;
};

export const addCommunityEvent = async (data: any): Promise<string> => {
  const result = await createDoc<any>("communityEvents", data);
  return (result as any).id as string;
};

export const updateCommunityEvent = async (id: string, data: any): Promise<void> => {
  await apiUpdateDoc("communityEvents", id, data);
};

export const getCommunityEventMessages = async (eventId: string): Promise<any[]> => {
  const all = await listDocs<any>("communityEventMessages", {
    field: "eventId",
    value: eventId,
  });
  return d<any[]>(all).sort((a, b) => {
    const ta = a.timestamp?.toMillis?.() ?? 0;
    const tb = b.timestamp?.toMillis?.() ?? 0;
    return ta - tb;
  });
};

export const updateCommunityEventMessage = async (id: string, data: any): Promise<void> => {
  await apiUpdateDoc("communityEventMessages", id, data);
};

/** Returns image URLs stored on the event document (imageUrls field). */
export const getEventImages = async (eventId: string): Promise<string[]> => {
  const event = await getCommunityEvent(eventId);
  return event?.imageUrls ?? [];
};

// ─────────────────────────────────────────────────────────────────────────────
// Newsletters
// ─────────────────────────────────────────────────────────────────────────────
export const getNewsletters = async (): Promise<any[]> => {
  const all = await listDocs<any>("newsletters");
  return d<any[]>(all).sort(
    (a, b) =>
      (b.newsletterDate?.toMillis?.() ?? 0) - (a.newsletterDate?.toMillis?.() ?? 0),
  );
};

export const getNewsletter = async (id: string): Promise<any | null> => {
  const result = await apiGetDoc<any>("newsletters", id);
  return result ? d<any>(result) : null;
};

export const addNewsletter = async (data: any): Promise<string> => {
  const result = await createDoc<any>("newsletters", data);
  return (result as any).id as string;
};

export const updateNewsletter = async (id: string, data: any): Promise<void> => {
  await apiUpdateDoc("newsletters", id, data);
};

export const deleteNewsletter = async (id: string): Promise<void> => {
  await apiDeleteDoc("newsletters", id);
};

export const getNewsletterTemplate = async (): Promise<any | null> => {
  return apiGetDoc<any>("settings", "newsletterTemplate");
};

export const saveNewsletterTemplate = async (data: any): Promise<void> => {
  await setDocById("settings", "newsletterTemplate", data);
};

export const getAppSettings = async (): Promise<any[]> => {
  return listDocs<any>("appSettings");
};

/** Proxy Firebase Callable Function: sync_klaviyo_emails */
export const syncKlaviyoEmails = async (data: any): Promise<any> => {
  return apiPost("/api/functions/sync_klaviyo_emails", data);
};

/** Proxy Firebase Callable Function: create_klaviyo_template */
export const createKlaviyoTemplate = async (data: any): Promise<any> => {
  return apiPost("/api/functions/create_klaviyo_template", data);
};

// ─────────────────────────────────────────────────────────────────────────────
// Promotions
// ─────────────────────────────────────────────────────────────────────────────
export const getPromotions = async (): Promise<any[]> => {
  const all = await listDocs<any>("promotions");
  return d<any[]>(all);
};

export const addPromotion = async (data: any): Promise<string> => {
  const result = await createDoc<any>("promotions", data);
  return (result as any).id as string;
};

export const updatePromotion = async (id: string, data: any): Promise<void> => {
  await apiUpdateDoc("promotions", id, data);
};

export const deletePromotion = async (id: string): Promise<void> => {
  await apiDeleteDoc("promotions", id);
};

// ─────────────────────────────────────────────────────────────────────────────
// Video Series (stored as content documents)
// ─────────────────────────────────────────────────────────────────────────────
export const addVideoSeries = async (data: any): Promise<string> => {
  const result = await createDoc<any>("content", data);
  return (result as any).id as string;
};

export const updateVideoSeries = async (id: string, data: any): Promise<void> => {
  await apiUpdateDoc("content", id, data);
};

// ─────────────────────────────────────────────────────────────────────────────
// Company invitations and additional company operations
// ─────────────────────────────────────────────────────────────────────────────
export const addCompany = async (data: any): Promise<string> => {
  const result = await createDoc<any>("companies", data);
  return (result as any).id as string;
};

export const addCompanyUserInvitation = async (data: any): Promise<string> => {
  const result = await createDoc<any>("companyUserInvitations", data);
  return (result as any).id as string;
};

export const updateCompanyUserInvitation = async (id: string, data: any): Promise<void> => {
  await apiUpdateDoc("companyUserInvitations", id, data);
};

export const addPartnershipUserInvitation = async (data: any): Promise<string> => {
  const result = await createDoc<any>("partnershipUserInvitations", data);
  return (result as any).id as string;
};

export const addTrigger = async (data: any): Promise<string> => {
  const result = await createDoc<any>("triggers", data);
  return (result as any).id as string;
};

/** Call proxied Firebase Callable Function: generate_admin_account */
export const generateAdminAccount = async (data: any): Promise<any> => {
  return apiPost("/api/functions/generate_admin_account", data);
};

// ─────────────────────────────────────────────────────────────────────────────
// Content event tag helpers (fetch-then-update pattern for array operations)
// ─────────────────────────────────────────────────────────────────────────────
export const addContentEventTag = async (contentId: string, tag: string): Promise<void> => {
  const content = await apiGetDoc<any>("content", contentId);
  const currentEvents: string[] = content?.contentEvents || [];
  if (!currentEvents.includes(tag)) {
    await apiUpdateDoc("content", contentId, { contentEvents: [...currentEvents, tag] });
  }
};

export const removeContentEventTag = async (contentId: string, tag: string): Promise<void> => {
  const content = await apiGetDoc<any>("content", contentId);
  const currentEvents: string[] = (content?.contentEvents || []).filter((e: string) => e !== tag);
  await apiUpdateDoc("content", contentId, { contentEvents: currentEvents });
};

export const addCommunityEventTagToEvent = async (communityEventId: string, tag: string): Promise<void> => {
  const ev = await getCommunityEvent(communityEventId) as any;
  const current: string[] = ev?.contentEvents || [];
  if (!current.includes(tag)) {
    await updateCommunityEvent(communityEventId, { contentEvents: [...current, tag] });
  }
};

export const removeCommunityEventTagFromEvent = async (communityEventId: string, tag: string): Promise<void> => {
  const ev = await getCommunityEvent(communityEventId) as any;
  const current: string[] = (ev?.contentEvents || []).filter((e: string) => e !== tag);
  await updateCommunityEvent(communityEventId, { contentEvents: current });
};
